import { useState, useEffect, useMemo } from 'react';
import { useLocale } from '../../lib/locale';
import { Search, Filter, Star, Clock, ShoppingCart, LogOut, Heart, Sparkles, Leaf, X, Check, ChevronRight, Wifi, Car, Wind, Navigation, Locate, TrendingUp, Crosshair, Percent, Zap, Gift, Award, Receipt, Settings, Utensils } from 'lucide-react';
import type { User as UserType, Restaurant } from '../../App';
import { api } from '../../lib/api';
import { resolveAssetUrl } from '../../lib/assets';
// import { ImageWithFallback } from '../figma/ImageWithFallback';
import { LocationSelector } from './LocationSelector';
import { ProfileDropdown } from './ProfileDropdown';
import { CustomerSettings } from './CustomerSettings';
import { ToastContainer, useToast } from '../ui/Toast';

interface RestaurantDiscoveryProps {
  user: UserType;
  onLogout: () => void;
  onSelectRestaurant: (restaurant: Restaurant) => void;
  restaurantRatingOverrides?: Record<string, { rating: number; totalRatings: number }>;
  orderMode?: 'dine-out' | 'order-online' | null;
  onBackToModeSelection?: () => void;
  currentLocation: string;
  onLocationChange: (location: string) => void;
  onViewOrderHistory?: () => void;
  cartItemCount?: number;
  onOpenCart?: () => void;
}

export function RestaurantDiscovery({ user, onLogout, onSelectRestaurant, restaurantRatingOverrides = {}, orderMode, onBackToModeSelection, currentLocation, onLocationChange, onViewOrderHistory, cartItemCount = 0, onOpenCart }: RestaurantDiscoveryProps) {
  const { formatMoney } = useLocale();
  const { toasts, removeToast, warning } = useToast();
  const FAVORITES_STORAGE_KEY = 'customer_favorite_restaurants';
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [restaurantsLoading, setRestaurantsLoading] = useState(true);
  const [restaurantsError, setRestaurantsError] = useState('');
  const [coupons, setCoupons] = useState<any[]>([]);
  const [masterCuisineTypes, setMasterCuisineTypes] = useState<any[]>([]);
  const [masterAmenities, setMasterAmenities] = useState<any[]>([]);
  const [masterDietaryTags, setMasterDietaryTags] = useState<any[]>([]);
  const [masterRatingFilters, setMasterRatingFilters] = useState<any[]>([]);
  const [restaurantsReloadKey, setRestaurantsReloadKey] = useState(0);
  const [restaurantSearch, setRestaurantSearch] = useState('');
  const [selectedCuisines, setSelectedCuisines] = useState<string[]>([]);
  const [restaurantRatingFilter, setRestaurantRatingFilter] = useState<number>(0);
  const [restaurantSortBy, setRestaurantSortBy] = useState<'relevance' | 'rating' | 'delivery-time' | 'cost' | 'distance'>('relevance');
  const [restaurantCategory, setRestaurantCategory] = useState<'all' | 'top-rated' | 'fastest' | 'budget' | 'new'>('all');
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(FAVORITES_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.map((id) => String(id)) : [];
    } catch {
      return [];
    }
  });
  const [dietaryFilter, setDietaryFilter] = useState<string>(() => {
    const stored = localStorage.getItem('dietaryMode');
    return stored ? String(stored) : 'all';
  });
  const [onlyOpenNow, setOnlyOpenNow] = useState(false);
  const [onlyWithOffers, setOnlyWithOffers] = useState(false);
  const [budgetRange, setBudgetRange] = useState<'all' | 'budget' | 'mid' | 'premium'>('all');
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
  const [distanceFilter, setDistanceFilter] = useState<number>(10); // in km

  // Filter modal state
  const [showFilterModal, setShowFilterModal] = useState(false);
  
  // Settings state
  const [showSettings, setShowSettings] = useState<string | null>(null);
  
  // Food category state
  const [selectedFoodCategory, setSelectedFoodCategory] = useState<string | null>(null);

  // Landing page -> customer discovery bootstrap (cuisine filter)
  useEffect(() => {
    const raw = sessionStorage.getItem('customer_discovery_cuisine');
    if (!raw) return;
    const cuisine = String(raw || '').trim();
    sessionStorage.removeItem('customer_discovery_cuisine');
    if (!cuisine) return;

    setRestaurantCategory('all');
    setRestaurantSearch('');
    setSelectedFoodCategory(null);
    setSelectedCuisines([cuisine]);
  }, []);
  
  // Temporary filter states (for modal)
  const [tempCuisines, setTempCuisines] = useState<string[]>([]);
  const [tempRating, setTempRating] = useState<number>(0);
  const [tempDietary, setTempDietary] = useState<string>('all');
  const [tempOpenNow, setTempOpenNow] = useState(false);
  const [tempOffers, setTempOffers] = useState(false);
  const [tempBudget, setTempBudget] = useState<'all' | 'budget' | 'mid' | 'premium'>('all');
  const [tempAmenities, setTempAmenities] = useState<string[]>([]);
  const [tempDistance, setTempDistance] = useState<number>(10);

  const isRestaurantClosed = (restaurant: Restaurant): boolean => {
    const raw = (restaurant as any)?.isOpen ?? (restaurant as any)?.is_open;
    if (raw === false || raw === 0 || raw === '0' || String(raw).toLowerCase() === 'false') return true;
    if (raw === true || raw === 1 || raw === '1' || String(raw).toLowerCase() === 'true') return false;
    return false;
  };

  const handleRestaurantClick = (restaurant: Restaurant) => {
    if (isRestaurantClosed(restaurant)) {
      warning('Restaurant is closed.');
      return;
    }
    onSelectRestaurant(restaurant);
  };

  // Sync dietary filter with ProfileDropdown changes
  useEffect(() => {
    const handleDietaryModeChange = (event: CustomEvent) => {
      setDietaryFilter(String((event as any)?.detail ?? 'all'));
    };

    // Listen for custom dietary mode change events
    window.addEventListener('dietaryModeChange', handleDietaryModeChange as EventListener);

    return () => {
      window.removeEventListener('dietaryModeChange', handleDietaryModeChange as EventListener);
    };
  }, []);

  const normalizeDietaryValue = (raw: unknown): string => {
    const normalized = String(raw || '')
      .trim()
      .toLowerCase()
      .replace(/[\s_]+/g, '-');
    if (!normalized) return '';
    if (normalized === 'nonveg') return 'non-veg';
    return normalized;
  };

  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
    } catch {
      // ignore
    }
  }, [favorites]);

  // Food Categories with real images
  const foodCategories = [
    { 
      id: 'pizza', 
      name: 'Pizza', 
      image: 'https://images.unsplash.com/photo-1489564239502-7a532064e1c2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwaXp6YSUyMHBsYXRlfGVufDF8fHx8MTc2Nzg3MzU0OXww&ixlib=rb-4.1.0&q=80&w=1080',
      cuisines: ['Pizza', 'Italian']
    },
    { 
      id: 'burger', 
      name: 'Burgers', 
      image: 'https://images.unsplash.com/photo-1699236290868-8070cd393ba6?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXJnZXIlMjBwbGF0ZXxlbnwxfHx8fDE3Njc4NzM1NTB8MA&ixlib=rb-4.1.0&q=80&w=1080',
      cuisines: ['American', 'Fast Food', 'Burger']
    },
    { 
      id: 'biryani', 
      name: 'Biryani', 
      image: 'https://images.unsplash.com/photo-1633945274417-ab205ae69d10?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxiaXJ5YW5pJTIwcmljZXxlbnwxfHx8fDE3Njc4MzQ5ODl8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      cuisines: ['Indian', 'Biryani', 'North Indian']
    },
    { 
      id: 'chinese', 
      name: 'Chinese', 
      image: 'https://images.unsplash.com/photo-1767324672643-c4979362f922?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaGluZXNlJTIwbm9vZGxlcyUyMHBsYXRlfGVufDF8fHx8MTc2Nzg3MzU1MHww&ixlib=rb-4.1.0&q=80&w=1080',
      cuisines: ['Chinese', 'Asian']
    },
    { 
      id: 'north-indian', 
      name: 'North Indian', 
      image: 'https://images.unsplash.com/photo-1767114915989-c6ab3c8fc42e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxub3J0aCUyMGluZGlhbiUyMGN1cnJ5fGVufDF8fHx8MTc2Nzg4NDg2NHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      cuisines: ['North Indian', 'Indian', 'Curry']
    },
    { 
      id: 'south-indian', 
      name: 'South Indian', 
      image: 'https://images.unsplash.com/photo-1665660710687-b44c50751054?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxkb3NhJTIwc291dGglMjBpbmRpYW58ZW58MXx8fHwxNzY3NzkwMTU3fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      cuisines: ['South Indian', 'Indian']
    },
    { 
      id: 'pasta', 
      name: 'Pasta', 
      image: 'https://images.unsplash.com/photo-1703258581842-31608ecd6528?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwYXN0YSUyMGl0YWxpYW58ZW58MXx8fHwxNzY3ODQ3NjkwfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      cuisines: ['Italian', 'Pasta']
    },
    { 
      id: 'momos', 
      name: 'Momos', 
      image: 'https://images.unsplash.com/photo-1694850184798-320a8e10bb5e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb21vcyUyMGR1bXBsaW5nc3xlbnwxfHx8fDE3Njc3OTYwNDR8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      cuisines: ['Chinese', 'Asian', 'Street Food']
    },
    { 
      id: 'sandwich', 
      name: 'Sandwich', 
      image: 'https://images.unsplash.com/photo-1566925950657-6a225149fa90?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzYW5kd2ljaCUyMHBsYXRlfGVufDF8fHx8MTc2Nzc3OTMwNnww&ixlib=rb-4.1.0&q=80&w=1080',
      cuisines: ['Sandwich', 'Cafe', 'American']
    },
    { 
      id: 'sushi', 
      name: 'Sushi', 
      image: 'https://images.unsplash.com/photo-1730325559618-940c72290ef0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzdXNoaSUyMGphcGFuZXNlfGVufDF8fHx8MTc2Nzg2ODE5MHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      cuisines: ['Japanese', 'Asian', 'Sushi']
    },
    { 
      id: 'kebabs', 
      name: 'Kebabs', 
      image: 'https://images.unsplash.com/photo-1626323109252-0adb3b46692b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxrZWJhYiUyMHNrZXdlcnN8ZW58MXx8fHwxNzY3ODg0ODY1fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      cuisines: ['Indian', 'Middle Eastern', 'BBQ']
    },
    { 
      id: 'beverages', 
      name: 'Beverages', 
      image: 'https://images.unsplash.com/photo-1618263655794-f2e728e59521?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb2ZmZWUlMjBiZXZlcmFnZXxlbnwxfHx8fDE3Njc4NTI3Mzd8MA&ixlib=rb-4.1.0&q=80&w=1080',
      cuisines: ['Cafe', 'Beverages']
    },
    { 
      id: 'desserts', 
      name: 'Cakes', 
      image: 'https://images.unsplash.com/photo-1673551493011-2b5f771013d4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpY2UlMjBjcmVhbSUyMGRlc3NlcnR8ZW58MXx8fHwxNzY3Nzk0MDgzfDA&ixlib=rb-4.1.0&q=80&w=1080',
      cuisines: ['Desserts', 'Bakery', 'Cafe']
    },
    { 
      id: 'ice-cream', 
      name: 'Ice Cream', 
      image: 'https://images.unsplash.com/photo-1673551493011-2b5f771013d4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpY2UlMjBjcmVhbSUyMGRlc3NlcnR8ZW58MXx8fHwxNzY3Nzk0MDgzfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      cuisines: ['Desserts', 'Ice Cream', 'Cafe']
    },
    { 
      id: 'breakfast', 
      name: 'Breakfast', 
      image: 'https://images.unsplash.com/photo-1550041503-367c95109143?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwYW5jYWtlJTIwYnJlYWtmYXN0fGVufDF8fHx8MTc2NzgwMjAyM3ww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      cuisines: ['Breakfast', 'Cafe', 'American']
    },
    { 
      id: 'healthy', 
      name: 'Healthy', 
      image: 'https://images.unsplash.com/photo-1640718153995-db4d3f0a6337?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxoZWFsdGh5JTIwYm93bCUyMHNhbGFkfGVufDF8fHx8MTc2Nzg3Nzc4M3ww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      cuisines: ['Healthy', 'Salad', 'Organic']
    },
    { 
      id: 'soup', 
      name: 'Soup', 
      image: 'https://images.unsplash.com/photo-1701109876066-7fc0c08da447?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzb3VwJTIwYm93bHxlbnwxfHx8fDE3Njc4NTE5MjR8MA&ixlib=rb-4.1.0&q=80&w=1080',
      cuisines: ['Chinese', 'Asian', 'Healthy']
    },
    { 
      id: 'fried-chicken', 
      name: 'Fried Chicken', 
      image: 'https://images.unsplash.com/photo-1670688866261-db6697858df8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxmcmllZCUyMGNoaWNrZW4lMjBmYXN0JTIwZm9vZHxlbnwxfHx8fDE3Njc4ODQ4Njd8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      cuisines: ['Fast Food', 'American', 'Chicken']
    },
    { 
      id: 'tacos', 
      name: 'Tacos', 
      image: 'https://images.unsplash.com/photo-1546853846-53490ede35b4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0YWNvcyUyMG1leGljYW58ZW58MXx8fHwxNzY3ODA2NzE1fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      cuisines: ['Mexican', 'Fast Food', 'Street Food']
    },
    { 
      id: 'salad', 
      name: 'Rolls', 
      image: 'https://images.unsplash.com/photo-1669472546359-418a98630699?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzYWxhZCUyMHBsYXRlfGVufDF8fHx8MTc2Nzg3MzU1MXww&ixlib=rb-4.1.0&q=80&w=1080',
      cuisines: ['Indian', 'Street Food', 'Healthy']
    },
  ];

  useEffect(() => {
    let mounted = true;

    const isUuidLike = (value: string) => /^[0-9a-f]{8}-/i.test(value.trim());

    const parseCuisineList = (value: any): string[] =>
      (Array.isArray(value)
        ? value
        : String(value || '')
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
      )
        .map((item: any) => String(item || '').trim())
        .filter(Boolean);

    const resolveCuisineNames = (value: any, map: Record<string, string>): string[] =>
      Array.from(
        new Set(
          parseCuisineList(value)
            .map((item) => (isUuidLike(item) ? map[item] || '' : item))
            .map((item) => item.trim())
            .filter(Boolean)
        )
      );

    const haversineKm = (
      from: { latitude: number; longitude: number },
      to: { latitude: number; longitude: number }
    ): number => {
      const toRad = (deg: number) => (deg * Math.PI) / 180;
      const R = 6371; // km
      const dLat = toRad(to.latitude - from.latitude);
      const dLng = toRad(to.longitude - from.longitude);
      const lat1 = toRad(from.latitude);
      const lat2 = toRad(to.latitude);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    };

    const toRestaurantCard = (
      row: any,
      index: number,
      map: Record<string, string>,
      dietaryTagIdToValue: Record<string, string>,
      userCoords: { latitude: number; longitude: number } | null
    ): Restaurant => {
      const toNumberSafe = (value: unknown, fallback = 0): number => {
        if (value === null || value === undefined) return fallback;
        if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;
        if (typeof value === 'bigint') return Number(value);
        const raw =
          typeof value === 'string'
            ? value
            : typeof (value as any)?.toString === 'function'
            ? (value as any).toString()
            : String(value);
        const parsed = Number(String(raw).trim());
        return Number.isFinite(parsed) ? parsed : fallback;
      };

      const openTime = normalizeRestaurantTime(row?.openTime || row?.open_time, '09:00');
      const closeTime = normalizeRestaurantTime(row?.closeTime || row?.close_time, '22:00');
      const deliveryRaw = String(row?.deliveryTime || row?.delivery_time || '30-45').trim();
      const minimumOrder = toNumberSafe(row?.minimumOrder ?? row?.minimum_order, 0);
      const deliveryFee = toNumberSafe(row?.deliveryFee ?? row?.delivery_fee, 0);
      const packagingCharges = toNumberSafe(row?.packagingCharges ?? row?.packaging_charges, 0);
      const rating = toNumberSafe(row?.rating, 0);
      const isActive = Boolean(row?.isActive ?? row?.is_active ?? true);
      const imageCandidate = String(row?.coverImage || row?.cover_image || row?.banner || row?.logo || '').trim();
      const imageUrl =
        resolveAssetUrl(imageCandidate) ||
        imageCandidate ||
        'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200';

      const parseStringArray = (value: unknown): string[] => {
        if (Array.isArray(value)) return value.map((v) => String(v)).map((v) => v.trim()).filter(Boolean);
        if (typeof value === 'string') {
          const raw = value.trim();
          if (!raw) return [];
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
              return parsed.map((v) => String(v)).map((v) => v.trim()).filter(Boolean);
            }
          } catch {
            // ignore
          }
          return raw
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean);
        }
        return [];
      };

      const foodTypes = Array.isArray(row?.foodTypes || row?.food_types)
        ? (row?.foodTypes || row?.food_types)
        : [];
      const foodTypeIds = parseStringArray(row?.foodTypeIds ?? row?.food_type_ids ?? row?.food_type_id);
      const amenityIds = parseStringArray(row?.amenities ?? row?.amenityIds ?? row?.amenity_ids);

      const dietaryTags = new Set<string>();
      for (const ft of Array.isArray(foodTypes) ? foodTypes : []) {
        const value = normalizeDietaryValue(ft?.code || ft?.name || ft?.label || ft?.title);
        if (value) dietaryTags.add(value);
      }
      for (const id of foodTypeIds) {
        const value = dietaryTagIdToValue[id];
        if (value) dietaryTags.add(value);
      }
      if (Boolean(row?.isPureVeg || row?.is_pure_veg || false)) {
        dietaryTags.add('veg');
      }

      const latitude = Number(row?.latitude ?? row?.lat ?? row?.restaurantLatitude ?? row?.restaurant_latitude);
      const longitude = Number(row?.longitude ?? row?.lng ?? row?.restaurantLongitude ?? row?.restaurant_longitude);
      const hasCoords = Number.isFinite(latitude) && Number.isFinite(longitude);

      const apiDistanceValue = row?.distance !== undefined && row?.distance !== null ? Number(row.distance) : null;
      const resolvedDistance = Number.isFinite(apiDistanceValue)
        ? (apiDistanceValue as number)
        : userCoords && hasCoords
        ? haversineKm(userCoords, { latitude, longitude })
        : Number((1.2 + (index % 8) * 0.6).toFixed(1));

      const integrationSetup = row?.integrationSetup ?? row?.integration_setup ?? null;

      return {
        id: String(row?.id || `dynamic-${index}`),
        name: String(row?.name || 'Restaurant'),
        description: String(row?.description || 'Fresh food delivered fast'),
        logo: resolveAssetUrl(String(row?.logo || '').trim()) || String(row?.logo || imageUrl),
        banner: imageUrl,
        coverImage: imageUrl,
        integrationSetup,
        integration_setup: integrationSetup,
        address: String(row?.address || ''),
        city: String(row?.city || 'City'),
        phone: String(row?.phone || ''),
        email: String(row?.email || ''),
        latitude: hasCoords ? latitude : undefined,
        longitude: hasCoords ? longitude : undefined,
        primaryColor: '#2563eb',
        secondaryColor: '#eff6ff',
        isActive,
        rating: rating > 0 ? rating : 0,
        totalOrders: Number(row?.totalOrders || 0),
        totalRatings: Number(row?.totalReviews || 0),
        cuisine: resolveCuisineNames(row?.cuisine || row?.cuisineType, map),
        openTime,
        closeTime,
        deliveryTime: deliveryRaw.includes('min') ? deliveryRaw : `${deliveryRaw} mins`,
        minimumOrder,
        deliveryFee,
        freeDeliveryAbove: minimumOrder > 0 ? minimumOrder * 2 : 500,
        taxRate: 5,
        packagingCharges,
        distance: Number.isFinite(resolvedDistance) ? Number(resolvedDistance.toFixed(1)) : undefined,
        isPureVeg: Boolean(row?.isPureVeg || row?.is_pure_veg || false),
        dietaryTags: Array.from(dietaryTags),
        foodTypeIds,
        foodTypes: Array.isArray(foodTypes) ? foodTypes : [],
        isOpen: (() => {
          const raw = row?.isOpen ?? row?.is_open;
          if (raw === false || raw === 0 || raw === '0' || String(raw).toLowerCase() === 'false') return false;
          if (raw === true || raw === 1 || raw === '1' || String(raw).toLowerCase() === 'true') return true;
          return isActive;
        })(),
        hasOffers: false,
        amenities: amenityIds,
      };
    };

    const getStoredCoordinates = (): { latitude: number; longitude: number } | null => {
      const latRaw = localStorage.getItem('customer_latitude');
      const lngRaw = localStorage.getItem('customer_longitude');
      if (!latRaw || !lngRaw) return null;
      const latitude = Number(latRaw);
      const longitude = Number(lngRaw);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
      return { latitude, longitude };
    };

    const loadRestaurants = async () => {
      try {
        if (mounted) {
          setRestaurantsLoading(true);
          setRestaurantsError('');
        }
        const userCoords = getStoredCoordinates();
        const [restaurantResponse, cuisineTypesResponse, couponsResponse, amenitiesResponse, dietaryTagsResponse, ratingFiltersResponse] = await Promise.allSettled([
          api.restaurants.getAll({ page: 1, limit: 500 } as any),
          api.masterData.cuisineTypes.getAll(),
          api.coupons.getAll(),
          api.masterData.amenities.getAll(),
          (api.masterData as any).dietaryTags?.getAll ? (api.masterData as any).dietaryTags.getAll() : api.masterData.dietaryPreferences.getAll(),
          (api.masterData as any).customerRatingFilters?.getAll ? (api.masterData as any).customerRatingFilters.getAll() : Promise.resolve({ success: true, data: [] }),
        ]);
        const nextCuisineNameMap =
          cuisineTypesResponse.status === 'fulfilled' && Array.isArray((cuisineTypesResponse.value as any)?.data)
            ? (cuisineTypesResponse.value as any).data.reduce((acc: Record<string, string>, item: any) => {
                const id = String(item?.id || '').trim();
                const name = String(item?.name || item?.code || '').trim();
                if (id && name) acc[id] = name;
                return acc;
              }, {})
            : {};

        const response = restaurantResponse.status === 'fulfilled' ? restaurantResponse.value : null;
        const rawRows = Array.isArray((response as any)?.data)
          ? (response as any).data
          : Array.isArray((response as any)?.data?.restaurants)
          ? (response as any).data.restaurants
          : [];

        const nextCoupons =
          couponsResponse.status === 'fulfilled' && Array.isArray((couponsResponse.value as any)?.data)
            ? (couponsResponse.value as any).data
            : [];

        const nextCuisineTypes =
          cuisineTypesResponse.status === 'fulfilled' && Array.isArray((cuisineTypesResponse.value as any)?.data)
            ? (cuisineTypesResponse.value as any).data
            : [];

        const nextAmenities =
          amenitiesResponse.status === 'fulfilled' && Array.isArray((amenitiesResponse.value as any)?.data)
            ? (amenitiesResponse.value as any).data
            : [];

        const nextDietaryTags =
          dietaryTagsResponse.status === 'fulfilled' && Array.isArray((dietaryTagsResponse.value as any)?.data)
            ? (dietaryTagsResponse.value as any).data
            : [];

        const dietaryTagIdToValue = nextDietaryTags.reduce((acc: Record<string, string>, tag: any) => {
          const id = String(tag?.id || '').trim();
          const value = normalizeDietaryValue(tag?.code || tag?.name || tag?.label || tag?.title);
          if (id && value) acc[id] = value;
          return acc;
        }, {});

        const mapped = rawRows
          .map((row: any, index: number) =>
            toRestaurantCard(row, index, nextCuisineNameMap, dietaryTagIdToValue, userCoords)
          )
          .filter((row: Restaurant) => row.isActive);

        const nextRatingFilters =
          ratingFiltersResponse.status === 'fulfilled' && Array.isArray((ratingFiltersResponse.value as any)?.data)
            ? (ratingFiltersResponse.value as any).data
            : [];

        if (mounted) {
          setRestaurants(mapped);
          setCoupons(nextCoupons);
          setMasterCuisineTypes(nextCuisineTypes);
          setMasterAmenities(nextAmenities);
          setMasterDietaryTags(nextDietaryTags);
          setMasterRatingFilters(nextRatingFilters);
        }
      } catch (error: any) {
        if (mounted) {
          setRestaurants([]);
          setCoupons([]);
          setMasterCuisineTypes([]);
          setMasterAmenities([]);
          setMasterDietaryTags([]);
          setMasterRatingFilters([]);
          setRestaurantsError(error?.message || 'Unable to load restaurants right now.');
        }
      } finally {
        if (mounted) setRestaurantsLoading(false);
      }
    };

    void loadRestaurants();
    return () => {
      mounted = false;
    };
  }, [restaurantsReloadKey]);

  // Theme change handler
  const ratingOptions = useMemo(() => {
    const normalized = (Array.isArray(masterRatingFilters) ? masterRatingFilters : [])
      .map((row: any) => ({
        value: Number(row?.value ?? row?.rating ?? row?.minRating ?? row?.min_rating ?? row?.threshold ?? 0),
        displayOrder: Number(row?.displayOrder ?? row?.display_order ?? 0) || 0,
        isActive: Boolean(row?.isActive ?? row?.is_active ?? true),
      }))
      .filter((row) => Number.isFinite(row.value) && row.value >= 0 && row.isActive)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0) || a.value - b.value);

    if (normalized.length > 0) return normalized.map((row) => row.value);
    return [0, 3, 3.5, 4, 4.5];
  }, [masterRatingFilters]);

  const dietaryOptions = useMemo(() => {
    const rows = Array.isArray(masterDietaryTags) ? masterDietaryTags : [];
    const mapped = rows
      .map((row: any) => {
        const value = normalizeDietaryValue(row?.code || row?.name || row?.label || row?.title || row?.id);
        const label = String(row?.name || row?.label || row?.title || row?.code || '').trim() || value;
        const isActive = Boolean(row?.isActive ?? row?.is_active ?? true);
        const displayOrder = Number(row?.displayOrder ?? row?.display_order ?? 0) || 0;
        return { value, label, isActive, displayOrder };
      })
      .filter((row) => row.value && row.isActive && row.value !== 'all')
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0) || a.label.localeCompare(b.label));

    const unique: Array<{ value: string; label: string }> = [];
    const seen = new Set<string>();
    for (const row of mapped) {
      if (seen.has(row.value)) continue;
      seen.add(row.value);
      unique.push({ value: row.value, label: row.label });
    }

    return [{ value: 'all', label: 'All' }, ...unique];
  }, [masterDietaryTags]);

  // Get cuisines from master (fallback: from restaurants)
  const allCuisines = useMemo(() => {
    const masterNames = (Array.isArray(masterCuisineTypes) ? masterCuisineTypes : [])
      .map((row: any) => String(row?.name || row?.label || row?.title || row?.code || '').trim())
      .filter(Boolean);

    const derivedNames = restaurants.flatMap((r) => r.cuisine || []).map((c) => String(c || '').trim()).filter(Boolean);

    return Array.from(new Set(masterNames.length ? masterNames : derivedNames)).sort((a, b) => a.localeCompare(b));
  }, [masterCuisineTypes, restaurants]);

  // Get amenities from master (fallback: common 3)
  const allAmenities = useMemo(() => {
    const fallback = [
      { id: 'wifi', label: 'Wi-Fi', icon: Wifi },
      { id: 'parking', label: 'Parking', icon: Car },
      { id: 'ac', label: 'AC', icon: Wind },
    ];

    const rows = Array.isArray(masterAmenities) ? masterAmenities : [];
    if (!rows.length) return fallback;

    const toAmenityId = (raw: any) => {
      const rawId = String(raw?.id || '').trim();
      const isUuid =
        !!rawId &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rawId);
      if (isUuid) return rawId;

      return String(raw?.code || raw?.slug || raw?.key || raw?.name || raw?.label || rawId || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_');
    };

    const toIcon = (id: string) => {
      if (id.includes('wifi')) return Wifi;
      if (id.includes('park')) return Car;
      if (id.includes('ac') || id.includes('air')) return Wind;
      return Settings;
    };

    const mapped = rows
      .map((row: any) => {
        const id = toAmenityId(row);
        const label = String(row?.name || row?.label || row?.title || row?.code || '').trim() || id;
        const isActive = Boolean(row?.isActive ?? row?.is_active ?? true);
        const displayOrder = Number(row?.displayOrder ?? row?.display_order ?? 0) || 0;
        return { id, label, icon: toIcon(id), isActive, displayOrder };
      })
      .filter((row) => row.id && row.label && row.isActive)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0) || a.label.localeCompare(b.label));

    return mapped.length ? mapped : fallback;
  }, [masterAmenities]);

  // Open filter modal
  const openFilterModal = () => {
    // Copy current filters to temp
    setTempCuisines([...selectedCuisines]);
    setTempRating(restaurantRatingFilter);
    setTempDietary(dietaryFilter);
    setTempOpenNow(onlyOpenNow);
    setTempOffers(onlyWithOffers);
    setTempBudget(budgetRange);
    setTempAmenities([...selectedAmenities]);
    setTempDistance(distanceFilter);
    setShowFilterModal(true);
  };

  // Apply filters
  const applyFilters = () => {
    setSelectedCuisines([...tempCuisines]);
    setRestaurantRatingFilter(tempRating);
    setDietaryFilter(tempDietary);
    setOnlyOpenNow(tempOpenNow);
    setOnlyWithOffers(tempOffers);
    setBudgetRange(tempBudget);
    setSelectedAmenities([...tempAmenities]);
    setDistanceFilter(tempDistance);
    setShowFilterModal(false);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setTempCuisines([]);
    setTempRating(0);
    setTempDietary('all');
    setTempOpenNow(false);
    setTempOffers(false);
    setTempBudget('all');
    setTempAmenities([]);
    setTempDistance(10);
  };

  // Count active filters
  const activeFilterCount = 
    selectedCuisines.length +
    (restaurantRatingFilter > 0 ? 1 : 0) +
    (dietaryFilter !== 'all' ? 1 : 0) +
    (onlyOpenNow ? 1 : 0) +
    (onlyWithOffers ? 1 : 0) +
    (budgetRange !== 'all' ? 1 : 0) +
    selectedAmenities.length +
    (distanceFilter !== 10 ? 1 : 0);

  // Toggle amenity in modal
  const toggleTempAmenity = (amenityId: string) => {
    setTempAmenities(prev =>
      prev.includes(amenityId) ? prev.filter(a => a !== amenityId) : [...prev, amenityId]
    );
  };

  // Toggle cuisine in modal
  const toggleTempCuisine = (cuisine: string) => {
    setTempCuisines(prev =>
      prev.includes(cuisine) ? prev.filter(c => c !== cuisine) : [...prev, cuisine]
    );
  };

  const applyRestaurantRatingOverride = (restaurant: Restaurant): Restaurant => {
    const override = restaurantRatingOverrides[restaurant.id];
    if (!override) return restaurant;
    return {
      ...restaurant,
      rating: override.rating,
      totalRatings: override.totalRatings,
    };
  };

  const restaurantsWithOverrides = restaurants.map(applyRestaurantRatingOverride);

  const formatRestaurantRating = (rating: number | null | undefined): string => {
    const value = Number(rating ?? 0);
    return value > 0 ? value.toFixed(1) : 'New';
  };

  // Filter and sort restaurants
  let filteredRestaurants = restaurantsWithOverrides.filter(restaurant => {
    // Search filter (by name, cuisine, budget, rating, distance)
    if (restaurantSearch) {
      const searchLower = restaurantSearch.toLowerCase();
      const nameMatch = restaurant.name.toLowerCase().includes(searchLower);
      const cuisineMatch = restaurant.cuisine.some(c => c.toLowerCase().includes(searchLower));
      const budgetMatch = searchLower.includes('budget') && restaurant.minimumOrder <= 200;
      const ratingMatch = searchLower.includes('rating') || searchLower.includes('star');
      const distanceMatch = searchLower.includes('near') || searchLower.includes('km');
      
      if (!nameMatch && !cuisineMatch && !budgetMatch && !ratingMatch && !distanceMatch) {
        return false;
      }
    }

    // Cuisine filter
    if (selectedCuisines.length > 0 && 
        !selectedCuisines.some(cuisine => restaurant.cuisine.includes(cuisine))) {
      return false;
    }

    // Rating filter
    if (restaurantRatingFilter > 0 && restaurant.rating < restaurantRatingFilter) {
      return false;
    }

    // Dietary filter
    if (dietaryFilter !== 'all') {
      const tags = Array.isArray(restaurant.dietaryTags) ? restaurant.dietaryTags : [];
      if (tags.length > 0) {
        if (!tags.includes(dietaryFilter)) return false;
      } else {
        if (dietaryFilter === 'veg' && !restaurant.isPureVeg) return false;
        if (dietaryFilter === 'non-veg' && restaurant.isPureVeg) return false;
      }
    }

    // Open now filter
    if (onlyOpenNow && !restaurant.isOpen) {
      return false;
    }

    // Offers filter
    if (onlyWithOffers && !restaurant.hasOffers) {
      return false;
    }

    // Budget range filter
    if (budgetRange !== 'all') {
      if (budgetRange === 'budget' && restaurant.minimumOrder > 200) {
        return false;
      }
      if (budgetRange === 'mid' && (restaurant.minimumOrder <= 200 || restaurant.minimumOrder > 400)) {
        return false;
      }
      if (budgetRange === 'premium' && restaurant.minimumOrder <= 400) {
        return false;
      }
    }

    // Amenities filter
    if (selectedAmenities.length > 0) {
      if (!selectedAmenities.every(amenity => restaurant.amenities?.includes(amenity))) {
        return false;
      }
    }

    // Distance filter
    if (distanceFilter !== 10 && restaurant.distance && restaurant.distance > distanceFilter) {
      return false;
    }

    return true;
  });

  // Category filter
  if (restaurantCategory !== 'all') {
    switch (restaurantCategory) {
      case 'top-rated':
        filteredRestaurants = filteredRestaurants.filter(r => r.rating >= 4.5);
        break;
      case 'fastest':
        filteredRestaurants = filteredRestaurants.filter(r => 
          parseInt(r.deliveryTime) <= 30
        );
        break;
      case 'budget':
        filteredRestaurants = filteredRestaurants.filter(r => r.minimumOrder <= 200);
        break;
      case 'new':
        // For demo, show all (in real app, filter by date)
        break;
    }
  }

  // Sort restaurants
  const sortedRestaurants = [...filteredRestaurants].sort((a, b) => {
    switch (restaurantSortBy) {
      case 'rating':
        return b.rating - a.rating;
      case 'delivery-time':
        return parseInt(a.deliveryTime) - parseInt(b.deliveryTime);
      case 'cost':
        return a.minimumOrder - b.minimumOrder;
      case 'distance':
        return (a.distance || 999) - (b.distance || 999);
      default:
        return 0;
    }
  });

  const toggleFavorite = (restaurantId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavorites(prev =>
      prev.includes(restaurantId) ? prev.filter(id => id !== restaurantId) : [...prev, restaurantId]
    );
  };

  // Featured offers (API-driven)
  const featuredOffers = useMemo(() => {
    const safeRestaurants = Array.isArray(restaurantsWithOverrides) ? restaurantsWithOverrides : [];
    const safeCoupons = Array.isArray(coupons) ? coupons : [];

    const isActiveCoupon = (raw: any) => {
      const flag = raw?.isActive ?? raw?.is_active ?? raw?.active ?? raw?.status;
      if (typeof flag === 'boolean') return flag;
      const normalized = String(flag ?? 'true').trim().toLowerCase();
      return ['true', '1', 'yes', 'active', 'enabled'].includes(normalized);
    };

    const normalizedCoupons = safeCoupons
      .filter(isActiveCoupon)
      .map((raw) => {
        const discountTypeRaw = raw?.discountType ?? raw?.discount_type ?? raw?.type ?? raw?.couponType;
        const discountValueRaw = raw?.discountValue ?? raw?.discount_value ?? raw?.value ?? raw?.amount;
        return {
          discountType: String(discountTypeRaw || '').trim().toLowerCase(),
          discountValue: Number(discountValueRaw ?? 0) || 0,
        };
      });

    const maxPercent = normalizedCoupons
      .filter((c) => c.discountType.includes('percent'))
      .reduce((max, c) => Math.max(max, c.discountValue), 0);

    const maxFlat = normalizedCoupons
      .filter((c) => c.discountType.includes('flat') || c.discountType.includes('fixed') || c.discountType.includes('amount'))
      .reduce((max, c) => Math.max(max, c.discountValue), 0);

    const bogoCount = normalizedCoupons.filter((c) => c.discountType.includes('bogo') || c.discountType.includes('buy')).length;

    const freeDeliveryRestaurants = safeRestaurants.filter((r) => (r.deliveryFee ?? 0) <= 0 || (r.freeDeliveryAbove ?? 0) > 0);
    const freeDeliveryAboveValues = freeDeliveryRestaurants
      .map((r) => r.freeDeliveryAbove)
      .filter((value) => typeof value === 'number' && value > 0);
    const minFreeDeliveryAbove = freeDeliveryAboveValues.length ? Math.min(...freeDeliveryAboveValues) : null;

    const topRatedCount = safeRestaurants.filter((r) => (r.rating ?? 0) >= 4.5).length;

    const offerTitle =
      maxPercent > 0
        ? `Up to ${Math.round(maxPercent)}% OFF`
        : normalizedCoupons.length > 0
        ? `${normalizedCoupons.length} Offers`
        : 'Offers';
    const offerSubtitle =
      maxPercent > 0
        ? 'On selected restaurants'
        : normalizedCoupons.length > 0
        ? 'Apply at checkout'
        : 'New deals near you';

    const freeDeliverySubtitle =
      freeDeliveryRestaurants.length > 0
        ? minFreeDeliveryAbove
          ? `Orders above ${formatMoney(minFreeDeliveryAbove)}`
          : `${freeDeliveryRestaurants.length} restaurants`
        : 'Check nearby options';

    const savingsTitle =
      maxFlat > 0 ? `Save ${formatMoney(maxFlat)}` : bogoCount > 0 ? 'BOGO Deals' : 'Savings';
    const savingsSubtitle =
      maxFlat > 0
        ? 'With coupons'
        : bogoCount > 0
        ? `${bogoCount} offers available`
        : normalizedCoupons.length > 0
        ? `${normalizedCoupons.length} coupons`
        : 'Limited time';

    const topRatedSubtitle = topRatedCount > 0 ? `${topRatedCount} restaurants 4.5+` : '4.5+ restaurants';

    return [
      { id: 'offers', title: offerTitle, subtitle: offerSubtitle, cardTone: 'offer-tone-blue', iconTone: 'offer-icon-blue', icon: Percent },
      { id: 'free-delivery', title: 'Free Delivery', subtitle: freeDeliverySubtitle, cardTone: 'offer-tone-green', iconTone: 'offer-icon-green', icon: Zap },
      { id: 'savings', title: savingsTitle, subtitle: savingsSubtitle, cardTone: 'offer-tone-amber', iconTone: 'offer-icon-amber', icon: Gift },
      { id: 'top-rated', title: 'Top Rated', subtitle: topRatedSubtitle, cardTone: 'offer-tone-rose', iconTone: 'offer-icon-rose', icon: Award },
    ];
  }, [restaurantsWithOverrides, coupons, formatMoney]);

  // Featured offers (demo data)
  const featuredOffersStatic = [
    { id: 1, title: '50% OFF', subtitle: 'On first order', cardTone: 'offer-tone-blue', iconTone: 'offer-icon-blue', icon: Percent },
    { id: 2, title: 'Free Delivery', subtitle: 'Orders above ₹299', cardTone: 'offer-tone-green', iconTone: 'offer-icon-green', icon: Zap },
    { id: 3, title: 'Cashback', subtitle: 'Up to ₹100', cardTone: 'offer-tone-amber', iconTone: 'offer-icon-amber', icon: Gift },
    { id: 4, title: 'Top Rated', subtitle: '4.5+ Restaurants', cardTone: 'offer-tone-rose', iconTone: 'offer-icon-rose', icon: Award }
  ];

  // Get nearby restaurants (within 5km, sorted by distance)
  const nearbyRestaurants = restaurantsWithOverrides
    .filter(r => r.distance && r.distance <= 5)
    .sort((a, b) => (a.distance || 999) - (b.distance || 999))
    .slice(0, 6);

  // Popular restaurants (demo)
  const popularRestaurants = sortedRestaurants.filter(r => r.rating >= 4.3).slice(0, 3);
  const restaurantGridClass = 'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4';

  const renderRestaurantCard = (restaurant: Restaurant) => {
    const hasOffer = restaurant.hasOffers;
    const isFavorite = favorites.includes(restaurant.id);
    const isClosed = isRestaurantClosed(restaurant);

    return (
      <div
        key={restaurant.id}
        onClick={() => handleRestaurantClick(restaurant)}
        className={`group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 ${
          isClosed ? 'opacity-60 grayscale cursor-not-allowed' : 'cursor-pointer hover:-translate-y-0.5 hover:shadow-lg'
        }`}
      >
        <div className="relative h-48 bg-gradient-to-r from-slate-200 to-slate-300">
          {restaurant.banner && (
            <img src={restaurant.banner} alt="" className="h-full w-full object-cover" />
          )}
          <div className="absolute left-3 top-3 flex flex-col gap-2">
            <div
              className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-bold shadow-md ${
                isClosed ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
              }`}
            >
              {isClosed ? 'Offline' : 'Online'}
            </div>
            {hasOffer && (
              <div className="flex items-center gap-1.5 rounded-full bg-orange-600 px-3 py-1.5 text-xs font-bold text-white shadow-md">
                <Sparkles className="h-3.5 w-3.5" />
                50% OFF
              </div>
            )}
            {restaurant.isPureVeg && (
              <div className="flex items-center gap-1 rounded-full bg-green-600 px-2.5 py-1 text-xs font-bold text-white shadow-md">
                <Leaf className="h-3.5 w-3.5" />
                Pure Veg
              </div>
            )}
          </div>
          <button
            onClick={(e) => toggleFavorite(restaurant.id, e)}
            disabled={isClosed}
            className={`absolute right-3 top-3 rounded-full bg-white p-2.5 shadow-md transition-transform ${
              isClosed ? 'opacity-70 cursor-not-allowed' : 'hover:scale-110'
            }`}
          >
            <Heart className={`h-5 w-5 ${isFavorite ? 'fill-orange-500 text-orange-500' : 'text-slate-600'}`} />
          </button>
          <div className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 shadow-md">
            <Star className="h-4 w-4 fill-orange-500 text-orange-500" />
            <span className="font-semibold text-slate-900">{formatRestaurantRating(restaurant.rating)}</span>
          </div>
          {restaurant.distance && (
            <div className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-blue-600/95 px-3 py-1.5 text-white shadow-md">
              <Navigation className="h-4 w-4" />
              <span className="text-sm font-semibold">{restaurant.distance} km</span>
            </div>
          )}
        </div>

        <div className="flex flex-1 flex-col p-5">
          <div className="mb-3 flex items-start gap-3">
            <img src={restaurant.logo} alt={restaurant.name} className="h-12 w-12 rounded-lg border border-slate-100 object-cover shadow-sm" />
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-lg font-semibold text-slate-900 transition-colors group-hover:text-blue-600">{restaurant.name}</h3>
              <p className="mt-0.5 line-clamp-2 text-sm text-slate-600">{restaurant.description}</p>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap gap-1.5">
            {restaurant.cuisine.slice(0, 3).map((c) => (
              <span key={c} className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                {c}
              </span>
            ))}
            {restaurant.cuisine.length > 3 && (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                +{restaurant.cuisine.length - 3}
              </span>
            )}
          </div>

          <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-4 text-sm">
            <div className="flex items-center gap-1.5 font-semibold text-green-700">
              <Clock className="h-4 w-4" />
              {restaurant.deliveryTime}
            </div>
            <span className="font-bold text-orange-600">₹{restaurant.deliveryFee}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="customer-theme min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-950/95">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-200 p-3 dark:border-white/10 sm:p-6">
              <div>
                <h2 className="text-base font-bold text-slate-900 dark:text-white sm:text-xl">Filters</h2>
                <p className="mt-1 text-[11px] text-slate-600 dark:text-slate-300 sm:text-sm">Customize your search</p>
              </div>
              <button
                onClick={() => setShowFilterModal(false)}
                className="rounded-lg p-1.5 transition-colors hover:bg-slate-100 dark:hover:bg-white/10 sm:p-2"
              >
                <X className="h-5 w-5 text-slate-600 dark:text-slate-300 sm:h-6 sm:w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto space-y-4 p-3 sm:space-y-6 sm:p-6">
              {/* Dietary Preferences */}
              <div>
                <h3 className="mb-2 text-xs font-semibold text-slate-900 dark:text-white sm:mb-3 sm:text-base">Dietary Preferences</h3>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {dietaryOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setTempDietary(option.value)}
                      className={`rounded-full px-3 py-1.5 text-[11px] font-medium transition-all sm:px-4 sm:py-2 sm:text-sm ${
                        tempDietary === option.value
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-200 dark:hover:bg-white/15'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Filters */}
              <div>
                <h3 className="mb-2 text-xs font-semibold text-slate-900 dark:text-white sm:mb-3 sm:text-base">Quick Filters</h3>
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  <button
                    onClick={() => setTempOpenNow(!tempOpenNow)}
                    className={`rounded-xl border-2 p-3 text-left transition-all sm:p-4 ${
                      tempOpenNow
                        ? 'border-green-500 bg-green-50 text-green-900 dark:bg-green-500/10 dark:text-green-200'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-medium sm:text-sm">Open Now</span>
                      {tempOpenNow && <Check className="h-4 w-4 sm:h-5 sm:w-5" />}
                    </div>
                  </button>
                  <button
                    onClick={() => setTempOffers(!tempOffers)}
                    className={`rounded-xl border-2 p-3 text-left transition-all sm:p-4 ${
                      tempOffers
                        ? 'border-orange-500 bg-orange-50 text-orange-900 dark:bg-orange-500/10 dark:text-orange-200'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-medium sm:text-sm">Offers Available</span>
                      {tempOffers && <Check className="h-4 w-4 sm:h-5 sm:w-5" />}
                    </div>
                  </button>
                </div>
              </div>

              {/* Rating Filter */}
              <div>
                <h3 className="mb-2 text-xs font-semibold text-slate-900 dark:text-white sm:mb-3 sm:text-base">Minimum Rating</h3>
                <div className="grid grid-cols-5 gap-2 sm:gap-3">
                  {ratingOptions.map((rating) => (
                    <button
                      key={rating}
                      onClick={() => setTempRating(rating)}
                      className={`rounded-xl border-2 p-2.5 transition-all sm:p-3 ${
                        tempRating === rating
                          ? 'border-blue-500 bg-blue-50 text-blue-900 dark:bg-blue-500/10 dark:text-blue-200'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-1">
                        {rating > 0 && <Star className="h-3.5 w-3.5 fill-current sm:h-4 sm:w-4" />}
                        <span className="text-[11px] font-medium leading-none sm:text-sm">{rating === 0 ? 'Any' : rating}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Budget Range */}
              <div>
                <h3 className="mb-2 text-xs font-semibold text-slate-900 dark:text-white sm:mb-3 sm:text-base">Budget Range</h3>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
                  <button
                    onClick={() => setTempBudget('all')}
                    className={`rounded-xl border-2 p-3 text-left transition-all sm:p-4 ${
                      tempBudget === 'all'
                        ? 'border-blue-500 bg-blue-50 text-blue-900 dark:bg-blue-500/10 dark:text-blue-200'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10'
                    }`}
                  >
                    <p className="text-[11px] font-medium sm:text-sm">All</p>
                  </button>
                  <button
                    onClick={() => setTempBudget('budget')}
                    className={`rounded-xl border-2 p-3 text-left transition-all sm:p-4 ${
                      tempBudget === 'budget'
                        ? 'border-green-500 bg-green-50 text-green-900 dark:bg-green-500/10 dark:text-green-200'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10'
                    }`}
                  >
                    <p className="text-[11px] font-medium sm:text-sm">Budget</p>
                    <p className="mt-1 text-[10px] opacity-70 sm:text-xs">Under {formatMoney(200)}</p>
                  </button>
                  <button
                    onClick={() => setTempBudget('mid')}
                    className={`rounded-xl border-2 p-3 text-left transition-all sm:p-4 ${
                      tempBudget === 'mid'
                        ? 'border-blue-500 bg-blue-50 text-blue-900 dark:bg-blue-500/10 dark:text-blue-200'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10'
                    }`}
                  >
                    <p className="text-[11px] font-medium sm:text-sm">Mid-Range</p>
                    <p className="mt-1 text-[10px] opacity-70 sm:text-xs">{formatMoney(200)}-{formatMoney(400)}</p>
                  </button>
                  <button
                    onClick={() => setTempBudget('premium')}
                    className={`rounded-xl border-2 p-3 text-left transition-all sm:p-4 ${
                      tempBudget === 'premium'
                        ? 'border-orange-500 bg-orange-50 text-orange-900 dark:bg-orange-500/10 dark:text-orange-200'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10'
                    }`}
                  >
                    <p className="text-[11px] font-medium sm:text-sm">Premium</p>
                    <p className="mt-1 text-[10px] opacity-70 sm:text-xs">Above {formatMoney(400)}</p>
                  </button>
                </div>
              </div>

              {/* Distance Filter */}
              <div>
                <div className="mb-2 flex items-center justify-between sm:mb-3">
                  <h3 className="text-xs font-semibold text-slate-900 dark:text-white sm:text-base">Maximum Distance</h3>
                  <span className="text-[11px] font-semibold text-blue-600 sm:text-sm">{tempDistance} km</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="20"
                  value={tempDistance}
                  onChange={(e) => setTempDistance(parseInt(e.target.value))}
                  className="h-1.5 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-blue-600 sm:h-2"
                />
                <div className="mt-2 flex justify-between text-[11px] text-slate-500 dark:text-slate-400 sm:text-xs">
                  <span>1 km</span>
                  <span>20 km</span>
                </div>
              </div>

              {/* Amenities */}
              <div>
                <h3 className="mb-2 text-xs font-semibold text-slate-900 dark:text-white sm:mb-3 sm:text-base">
                  Amenities {tempAmenities.length > 0 && `(${tempAmenities.length})`}
                </h3>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {allAmenities.map((amenity) => (
                    <button
                      key={amenity.id}
                      onClick={() => toggleTempAmenity(amenity.id)}
                      className={`rounded-full px-3 py-1.5 text-[11px] font-medium transition-all sm:px-4 sm:py-2 sm:text-sm ${
                        tempAmenities.includes(amenity.id)
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-200 dark:hover:bg-white/15'
                      }`}
                    >
                      {amenity.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cuisines */}
              <div>
                <h3 className="mb-2 text-xs font-semibold text-slate-900 dark:text-white sm:mb-3 sm:text-base">
                  Cuisines {tempCuisines.length > 0 && `(${tempCuisines.length})`}
                </h3>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {allCuisines.map((cuisine) => (
                    <button
                      key={cuisine}
                      onClick={() => toggleTempCuisine(cuisine)}
                      className={`rounded-full px-3 py-1.5 text-[11px] font-medium transition-all sm:px-4 sm:py-2 sm:text-sm ${
                        tempCuisines.includes(cuisine)
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-200 dark:hover:bg-white/15'
                      }`}
                    >
                      {cuisine}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/5 sm:p-6">
              <div className="flex items-center gap-2.5 sm:gap-3">
                <button
                  onClick={clearAllFilters}
                  className="flex-1 rounded-xl border-2 border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-white/10 dark:bg-white/10 dark:text-slate-200 dark:hover:bg-white/15 sm:px-6 sm:py-3"
                >
                  Clear All
                </button>
                <button
                  onClick={applyFilters}
                  className="flex-1 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-lg shadow-blue-200 transition-colors hover:bg-blue-700 sm:px-6 sm:py-3"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="border-b border-slate-200 bg-white text-slate-900 dark:border-white/10 dark:bg-gradient-to-br dark:from-slate-950 dark:via-slate-900 dark:to-slate-800 dark:text-white">
        <div className="flex items-center justify-between gap-3 px-3 py-3 sm:px-4 sm:py-4">
          <button
            type="button"
            onClick={() => {
              onBackToModeSelection?.();
              if (window.location.pathname === '/') return;
              window.history.pushState({}, '', '/');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
            className="flex items-center gap-2 text-slate-900 transition-colors hover:text-slate-700 dark:text-white dark:hover:text-white/95"
            title="Go to Landing Page"
          >
            <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FC8019] to-[#E84545] flex items-center justify-center shadow-lg shadow-orange-500/20">
              <Utensils className="w-5 h-5 text-white" />
            </span>
            <span className="text-lg font-extrabold tracking-tight">FoodHub</span>
          </button>

          <div className="flex items-center gap-2 sm:gap-3 flex-nowrap justify-end min-w-0 overflow-visible">
            <div className="hidden sm:block">
              <LocationSelector currentLocation={currentLocation} onLocationChange={onLocationChange} variant="header" />
            </div>
            {onViewOrderHistory && (
              <button 
                onClick={onViewOrderHistory}
                className="rounded-lg bg-slate-900/5 p-2 transition-colors hover:bg-slate-900/10 dark:bg-white/20 dark:hover:bg-white/30 sm:p-2.5"
                title="Order History"
              >
                <Receipt className="h-4 w-4 text-slate-700 dark:text-white sm:h-5 sm:w-5" />
              </button>
            )}
            <button
              onClick={onOpenCart}
              className="relative rounded-lg bg-slate-900/5 p-2 transition-colors hover:bg-slate-900/10 dark:bg-white/20 dark:hover:bg-white/30 sm:p-2.5"
              title={cartItemCount > 0 ? 'View Cart' : 'Cart'}
            >
              <ShoppingCart className="h-4 w-4 text-slate-700 dark:text-white sm:h-5 sm:w-5" />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {cartItemCount}
                </span>
              )}
            </button>
            <ProfileDropdown
              user={user}
              onLogout={onLogout}
              onOpenSettings={(section) => setShowSettings(section || 'account')}
              onOpenOrderHistory={onViewOrderHistory}
              variant="compact"
            />
          </div>
        </div>

        <div className="px-3 pb-3 sm:px-4">
          <div className="flex justify-end sm:hidden">
            <LocationSelector currentLocation={currentLocation} onLocationChange={onLocationChange} variant="header" />
          </div>
        </div>
      </div>

      {/* Search Bar Section - Above What's on your mind */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-3 py-4 sm:px-4 sm:py-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            {/* Search Input */}
            <div className="flex-1 relative w-full">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 sm:left-4 sm:h-5 sm:w-5" />
              <input
                type="text"
                placeholder="Search restaurants, cuisines..."
                value={restaurantSearch}
                onChange={(e) => setRestaurantSearch(e.target.value)}
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 text-sm text-gray-700 placeholder-gray-400 transition-all focus:border-[#60A5FA] focus:outline-none focus:ring-2 focus:ring-[#60A5FA]/20 sm:rounded-xl sm:py-3 sm:pl-12 sm:pr-4 sm:text-base"
              />
            </div>

            <div className="flex w-full gap-3 sm:w-auto">
              {/* Filters Button */}
              <button
                onClick={openFilterModal}
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-all sm:flex-none sm:rounded-xl sm:px-5 sm:py-3 sm:text-base ${
                  activeFilterCount > 0
                    ? 'border-[#60A5FA] bg-[#60A5FA] text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <Filter className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <span className="ml-1 rounded-full bg-white px-1.5 py-0.5 text-[11px] font-bold text-[#60A5FA] sm:px-2 sm:text-xs">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* Sort Dropdown */}
              <div className="relative flex-1 sm:flex-none sm:min-w-[210px]">
                <select
                  value={restaurantSortBy}
                  onChange={(e) => setRestaurantSortBy(e.target.value as any)}
                  className="w-full cursor-pointer appearance-none rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm font-medium text-gray-700 transition-all hover:border-gray-400 focus:border-[#60A5FA] focus:outline-none focus:ring-2 focus:ring-[#60A5FA]/20 sm:rounded-xl sm:py-3 sm:pl-4 sm:pr-10 sm:text-base"
                >
                  <option value="relevance">Sort: Relevance</option>
                  <option value="rating">Sort: Rating</option>
                  <option value="delivery-time">Sort: Delivery Time</option>
                  <option value="cost">Sort: Cost</option>
                  <option value="distance">Sort: Distance</option>
                </select>
                <ChevronRight className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 rotate-90 text-gray-400 sm:right-3 sm:h-5 sm:w-5" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What's on your mind? - Food Categories Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 py-6 sm:py-8">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold">What's on your mind?</h2>
              <p className="mt-1 text-sm text-gray-600">Pick a category to quickly filter restaurants.</p>
            </div>
          </div>

          <div className="whats-mind-track flex overflow-x-auto gap-3 sm:gap-4 pb-2 no-scrollbar">
            {foodCategories.map((category) => {
              const isSelected = selectedFoodCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => {
                    if (selectedFoodCategory === category.id) {
                      // Deselect
                      setSelectedFoodCategory(null);
                      setSelectedCuisines([]);
                    } else {
                      // Select and filter by cuisine
                      setSelectedFoodCategory(category.id);
                      setSelectedCuisines(category.cuisines);
                    }
                  }}
                  className={`whats-mind-card ${isSelected ? 'is-selected' : ''}`}
                >
                  <div className="whats-mind-image">
                    <img 
                      src={category.image} 
                      alt={category.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="whats-mind-label">
                    {category.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Featured Offers Banner - Below "What's on your mind?" */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 py-4 sm:py-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {featuredOffers.map((offer) => (
              <div
                key={offer.id}
                className={`offer-card ${offer.cardTone}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`offer-icon ${offer.iconTone}`}>
                    <offer.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{offer.title}</p>
                    <p className="text-xs text-slate-600">{offer.subtitle}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Restaurants Grid */}
      <div className="w-full px-4 py-8">
        {restaurantsLoading ? (
          <div className="py-20 text-center text-gray-600">Loading restaurants...</div>
        ) : restaurantsError ? (
          <div className="py-20 text-center">
            <p className="text-red-600 font-semibold mb-2">{restaurantsError}</p>
            <p className="text-gray-600">Please try again in a moment.</p>
            <button
              onClick={() => setRestaurantsReloadKey((value) => value + 1)}
              className="mt-6 inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : sortedRestaurants.length > 0 ? (
          <>
            {/* Nearby Restaurants Section */}
            {nearbyRestaurants.length > 0 && restaurantCategory === 'all' && !restaurantSearch && activeFilterCount === 0 && (
              <div className="mb-12">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                        <Locate className="w-6 h-6 text-white" />
                      </div>
                      <h2 className="text-gray-900 text-2xl">Nearby Restaurants</h2>
                    </div>
                    <p className="text-gray-600 ml-14">Within 5 km • Open now • Quick delivery</p>
                  </div>
                </div>

                <div className="relative">
                  <div
                    className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-12"
                    style={{ background: 'linear-gradient(to left, var(--customer-surface-muted), transparent)' }}
                  ></div>

                  <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar scroll-smooth snap-x snap-mandatory scroll-px-4">
                    {nearbyRestaurants.map((restaurant) => {
                      const hasOffer = restaurant.hasOffers;
                      const isFavorite = favorites.includes(restaurant.id);
                      const isClosed = isRestaurantClosed(restaurant);

                      return (
                         <div
                           key={restaurant.id}
                           onClick={() => handleRestaurantClick(restaurant)}
                           className={`snap-start flex-shrink-0 w-[calc(100vw-2rem)] min-[360px]:w-80 bg-white rounded-xl shadow-md overflow-hidden transition-all group ${
                             isClosed ? 'opacity-60 grayscale cursor-not-allowed' : 'hover:shadow-2xl cursor-pointer'
                           }`}
                         >
                          <div className="h-40 bg-gradient-to-r from-orange-400 to-orange-500 relative">
                            {restaurant.banner && (
                              <img src={restaurant.banner} alt="" className="w-full h-full object-cover" />
                            )}
                            <div className="absolute top-2 left-2 z-10 flex flex-col gap-2">
                              <div
                                className={`px-2.5 py-1 rounded-full text-xs font-bold shadow-lg ${
                                  isClosed ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
                                }`}
                              >
                                {isClosed ? 'Offline' : 'Online'}
                              </div>
                            {hasOffer && (
                              <div className="bg-orange-600 text-white px-2.5 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                                <Sparkles className="w-3 h-3" />
                                50% OFF
                              </div>
                            )}
                            {restaurant.isPureVeg && (
                              <div className="bg-green-600 text-white px-2 py-0.5 rounded text-xs font-bold shadow-lg flex items-center gap-1">
                                <Leaf className="w-3 h-3" />
                                Pure Veg
                              </div>
                            )}
                            </div>
                            <div className="absolute top-2 right-2 bg-blue-600 text-white px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg z-10 animate-pulse">
                              <Crosshair className="w-4 h-4" />
                              <span className="font-bold text-sm">{restaurant.distance} km</span>
                            </div>
                            <button
                              onClick={(e) => toggleFavorite(restaurant.id, e)}
                              disabled={isClosed}
                              className={`absolute bottom-2 right-2 p-2 bg-white rounded-full shadow-lg transition-transform z-10 ${
                                isClosed ? 'opacity-70 cursor-not-allowed' : 'hover:scale-110'
                              }`}
                            >
                              <Heart className={`w-4 h-4 ${isFavorite ? 'fill-orange-500 text-orange-500' : 'text-gray-600'}`} />
                            </button>
                            <div className="absolute bottom-2 left-2 bg-white px-2.5 py-1 rounded-full flex items-center gap-1 shadow-lg z-10">
                              <Star className="w-3.5 h-3.5 text-orange-500 fill-orange-500" />
                              <span className="text-gray-900 font-bold text-sm">{formatRestaurantRating(restaurant.rating)}</span>
                            </div>
                          </div>
                          <div className="p-4">
                            <div className="flex items-start gap-3 mb-3">
                              <img src={restaurant.logo} alt={restaurant.name} className="w-12 h-12 rounded-lg object-cover shadow-sm border-2 border-gray-100" />
                              <div className="flex-1 min-w-0">
                                <h3 className="text-gray-900 mb-1 truncate group-hover:text-blue-600 transition-colors font-semibold text-lg">{restaurant.name}</h3>
                                <p className="text-gray-600 text-xs line-clamp-1">{restaurant.description}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1.5 mb-3">
                              {restaurant.cuisine.slice(0, 2).map((c) => (
                                <span key={c} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                                  {c}
                                </span>
                              ))}
                              {restaurant.cuisine.length > 2 && (
                                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                                  +{restaurant.cuisine.length - 2}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center justify-between text-xs pt-3 border-t border-gray-100">
                              <div className="flex items-center gap-1 text-gray-600 bg-green-50 px-2 py-1 rounded">
                                <Clock className="w-3.5 h-3.5 text-green-600" />
                                <span className="font-semibold text-green-700">{restaurant.deliveryTime}</span>
                              </div>
                              <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded">
                                <Navigation className="w-3.5 h-3.5 text-blue-600" />
                                <span className="font-semibold text-blue-700">{restaurant.distance} km</span>
                              </div>
                              <span className="text-orange-600 font-bold bg-orange-50 px-2 py-1 rounded">
                                {formatMoney(restaurant.deliveryFee)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-6 text-center">
                  <button
                    onClick={() => {
                      setRestaurantSortBy('distance');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl font-medium"
                  >
                    <Locate className="w-5 h-5" />
                    Show All Nearby Restaurants
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Popular Section */}
            {popularRestaurants.length > 0 && restaurantCategory === 'all' && !restaurantSearch && activeFilterCount === 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-gray-900 text-2xl">Popular Right Now</h2>
                </div>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {popularRestaurants.map((restaurant) => {
                    const hasOffer = restaurant.hasOffers;
                    const isFavorite = favorites.includes(restaurant.id);
                    const isClosed = isRestaurantClosed(restaurant);

                    return (
                      <div
                        key={restaurant.id}
                        onClick={() => handleRestaurantClick(restaurant)}
                        className={`bg-white rounded-xl shadow-md overflow-hidden transition-all group ${
                          isClosed ? 'opacity-60 grayscale cursor-not-allowed' : 'hover:shadow-2xl cursor-pointer'
                        }`}
                      >
                        <div className="h-48 bg-gradient-to-r from-orange-400 to-orange-500 relative">
                          {restaurant.banner && (
                            <img src={restaurant.banner} alt="" className="w-full h-full object-cover" />
                          )}
                          <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
                            <div
                              className={`px-3 py-1.5 rounded-full text-xs font-bold shadow-lg ${
                                isClosed ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
                              }`}
                            >
                              {isClosed ? 'Offline' : 'Online'}
                            </div>
                          {hasOffer && (
                            <div className="bg-orange-600 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg flex items-center gap-1.5">
                              <Sparkles className="w-4 h-4" />
                              50% OFF
                            </div>
                          )}
                          {restaurant.isPureVeg && (
                            <div className="bg-green-600 text-white px-2.5 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                              <Leaf className="w-3.5 h-3.5" />
                              Pure Veg
                            </div>
                          )}
                          </div>
                          <button
                            onClick={(e) => toggleFavorite(restaurant.id, e)}
                            disabled={isClosed}
                            className={`absolute top-3 right-3 p-2.5 bg-white rounded-full shadow-lg transition-transform ${
                              isClosed ? 'opacity-70 cursor-not-allowed' : 'hover:scale-110'
                            }`}
                          >
                            <Heart className={`w-5 h-5 ${isFavorite ? 'fill-orange-500 text-orange-500' : 'text-gray-600'}`} />
                          </button>
                          <div className="absolute bottom-3 left-3 bg-white px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                            <Star className="w-4 h-4 text-orange-500 fill-orange-500" />
                            <span className="text-gray-900 font-bold">{formatRestaurantRating(restaurant.rating)}</span>
                          </div>
                        </div>
                        <div className="p-5">
                          <div className="flex items-start gap-3 mb-3">
                            <img src={restaurant.logo} alt={restaurant.name} className="w-14 h-14 rounded-lg object-cover shadow-sm border-2 border-gray-100" />
                            <div className="flex-1 min-w-0">
                              <h3 className="text-gray-900 mb-1 truncate group-hover:text-blue-600 transition-colors font-bold text-lg">{restaurant.name}</h3>
                              <p className="text-gray-600 text-sm line-clamp-2">{restaurant.description}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1.5 mb-4">
                            {restaurant.cuisine.slice(0, 3).map((c) => (
                              <span key={c} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full font-medium">
                                {c}
                              </span>
                            ))}
                          </div>
                          <div className="flex items-center justify-between text-sm pt-4 border-t border-gray-100">
                            <div className="flex items-center gap-1.5 text-green-700 font-semibold">
                              <Clock className="w-4 h-4" />
                              {restaurant.deliveryTime}
                            </div>
                            {restaurant.distance && (
                              <div className="flex items-center gap-1.5 text-blue-700 font-semibold">
                                <Navigation className="w-4 h-4" />
                                {restaurant.distance} km away
                              </div>
                            )}
                            <span className="text-orange-600 font-bold">{formatMoney(restaurant.deliveryFee)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* All Restaurants */}
            <div>
              <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <h2 className="text-2xl font-semibold tracking-tight text-gray-900 md:text-3xl">
                  {restaurantSearch ? 'Search Results' : restaurantCategory !== 'all' ? `${restaurantCategory.charAt(0).toUpperCase() + restaurantCategory.slice(1).replace('-', ' ')} Restaurants` : 'All Restaurants'}
                </h2>
                <div className="flex items-center justify-between gap-3 sm:justify-end">
                  <span className="rounded-full border border-gray-200 bg-white/80 px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm">
                    {sortedRestaurants.length} restaurant{sortedRestaurants.length !== 1 ? 's' : ''} found
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {sortedRestaurants.map((restaurant) => {
                  const hasOffer = restaurant.hasOffers;
                  const isFavorite = favorites.includes(restaurant.id);
                  const isClosed = isRestaurantClosed(restaurant);

                  return (
                    <div
                      key={restaurant.id}
                      onClick={() => handleRestaurantClick(restaurant)}
                      className={`group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all ${
                        isClosed
                          ? 'cursor-not-allowed opacity-60 grayscale'
                          : 'cursor-pointer hover:-translate-y-0.5 hover:shadow-xl'
                      }`}
                    >
                      <div className="relative h-44 bg-gray-200">
                        {restaurant.banner && (
                          <img src={restaurant.banner} alt="" className="w-full h-full object-cover" />
                        )}
                        <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
                          <div
                            className={`rounded-full px-3 py-1 text-xs font-semibold shadow-md ${
                              isClosed ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'
                            }`}
                          >
                            {isClosed ? 'Offline' : 'Online'}
                          </div>
                        {hasOffer && (
                          <div className="flex items-center gap-1.5 rounded-full bg-orange-600 px-3 py-1 text-xs font-semibold text-white shadow-md">
                            <Sparkles className="h-4 w-4" />
                            50% OFF
                          </div>
                        )}
                        {restaurant.isPureVeg && (
                          <div className="flex items-center gap-1 rounded-full bg-green-600 px-2.5 py-1 text-xs font-semibold text-white shadow-md">
                            <Leaf className="h-3.5 w-3.5" />
                            Pure Veg
                          </div>
                        )}
                        </div>
                        <button
                          onClick={(e) => toggleFavorite(restaurant.id, e)}
                          disabled={isClosed}
                          className={`absolute top-3 right-3 p-2.5 bg-white rounded-full shadow-lg transition-transform ${
                            isClosed ? 'opacity-70 cursor-not-allowed' : 'hover:scale-110'
                          }`}
                        >
                          <Heart className={`w-5 h-5 ${isFavorite ? 'fill-orange-500 text-orange-500' : 'text-gray-600'}`} />
                        </button>
                        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-gray-900 shadow-md">
                          <Star className="h-4 w-4 fill-orange-500 text-orange-500" />
                          <span className="text-gray-900 font-bold">{formatRestaurantRating(restaurant.rating)}</span>
                        </div>
                      </div>
                      <div className="flex flex-1 flex-col p-5">
                        <div className="mb-3 flex items-start gap-3">
                          <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-white ring-1 ring-black/5">
                            <img src={restaurant.logo} alt={restaurant.name} className="h-full w-full object-cover" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="mb-1 truncate text-lg font-semibold text-gray-900 transition-colors group-hover:text-blue-600">
                              {restaurant.name}
                            </h3>
                            <p className="line-clamp-2 text-sm text-gray-600">{restaurant.description}</p>
                          </div>
                        </div>

                        <div className="mb-4 flex flex-wrap gap-2">
                          {restaurant.cuisine.slice(0, 3).map((c) => (
                            <span key={c} className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                              {c}
                            </span>
                          ))}
                        </div>

                        <div className="mt-auto flex items-center justify-between gap-3 border-t border-gray-100 pt-4 text-sm">
                          <div className="flex items-center gap-1.5 font-semibold text-emerald-700">
                            <Clock className="h-4 w-4" />
                            {restaurant.deliveryTime}
                          </div>
                          {restaurant.distance && (
                            <div className="flex items-center gap-1.5 font-semibold text-blue-700">
                              <Navigation className="h-4 w-4" />
                              {restaurant.distance} km away
                            </div>
                          )}
                          <span className="font-bold text-orange-600">{formatMoney(restaurant.deliveryFee)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
              <Search className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-gray-900 text-xl mb-2">No restaurants found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your filters or search criteria</p>
            <button
              onClick={() => {
                setRestaurantSearch('');
                setSelectedCuisines([]);
                setRestaurantRatingFilter(0);
                setDietaryFilter('all');
                setOnlyOpenNow(false);
                setOnlyWithOffers(false);
                setBudgetRange('all');
                setSelectedAmenities([]);
                setDistanceFilter(10);
                setRestaurantCategory('all');
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Customer Settings Modal */}
      {showSettings && (
        <CustomerSettings
          user={user}
          onClose={() => setShowSettings(null)}
          onLogout={onLogout}
          initialSection={showSettings as any}
        />
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
const normalizeRestaurantTime = (value: unknown, fallback: string): string => {
  const raw = String(value ?? '').trim();
  if (!raw) return fallback;
  const matched = raw.match(/(\d{2}):(\d{2})(?::\d{2})?/);
  return matched ? `${matched[1]}:${matched[2]}` : fallback;
};
